import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, Lock, Loader2, ShieldCheck, Save } from "lucide-react";

export function ApiKeysTab() {
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [info, setInfo] = useState(null);
  const [secret, setSecret] = useState("");
  const [publishable, setPublishable] = useState("");
  const [busy, setBusy] = useState(false);

  const loadKeys = async () => {
    try { const { data } = await api.get("/admin/api-keys"); setInfo(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Pa t kapab chaje kle yo"); }
  };

  const unlock = async () => {
    if (!code.trim()) { toast.error("Antre kòd la"); return; }
    setBusy(true);
    try {
      await api.post("/admin/api-keys/verify-code", { code: code.trim() });
      setUnlocked(true); await loadKeys();
      toast.success("Aksè debloke");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!secret.trim() && !publishable.trim()) { toast.error("Antre omwen yon kle"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/api-keys", {
        access_code: code.trim(),
        stripe_secret_key: secret.trim() || undefined,
        stripe_publishable_key: publishable.trim() || undefined,
      });
      setInfo({ ...info, ...data });
      setSecret(""); setPublishable("");
      toast.success("Kle yo anrejistre");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto mt-8 border border-[#222] bg-[#0F0F0F] p-6 rounded-lg" data-testid="api-keys-lock">
        <div className="flex items-center gap-2 mb-2"><Lock className="h-5 w-5 text-[#f5a623]" />
          <h3 className="font-heading font-semibold text-white">Kle API — Zòn Pwoteje</h3></div>
        <p className="text-xs text-[#888] mb-4">Antre kòd aksè a pou w ka wè epi modifye kle API yo.</p>
        <Input type="password" value={code} onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && unlock()} placeholder="Kòd aksè"
          data-testid="api-keys-code-input" className="bg-[#0A0A0A] border-[#222] h-11 rounded-sm mb-3" />
        <Button onClick={unlock} disabled={busy} data-testid="api-keys-unlock-button"
          className="w-full h-11 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="mr-2 h-4 w-4" /> Debloke</>}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5" data-testid="api-keys-tab">
      <div className="flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-[#00F593]" />
        <h3 className="font-heading font-semibold text-white">Kle API Stripe</h3>
      </div>

      {info && (
        <div className="border border-[#222] bg-[#0F0F0F] p-4 rounded-lg space-y-2" data-testid="api-keys-current">
          <div className="font-mono-term text-xs text-[#888] uppercase tracking-widest">Kle aktif yo</div>
          <div className="font-mono-term text-sm text-white">Secret: <span className="text-[#8fb4e6]">{info.stripe_secret_masked || "— (poko mete)"}</span></div>
          <div className="font-mono-term text-sm text-white">Publishable: <span className="text-[#8fb4e6]">{info.stripe_publishable_masked || "— (poko mete)"}</span></div>
          <div className="font-mono-term text-[11px] text-[#666]">Sous: {info.source === "db" ? "Baz done (Admin)" : "Anviwònman (.env)"}</div>
        </div>
      )}

      <div className="border border-[#222] bg-[#0F0F0F] p-5 rounded-lg space-y-4">
        <div>
          <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#888]">Stripe Secret Key (sk_...)</label>
          <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
            placeholder="sk_live_... oswa sk_test_..." data-testid="api-keys-secret-input"
            className="mt-1.5 bg-[#0A0A0A] border-[#222] h-11 rounded-sm font-mono-term" />
        </div>
        <div>
          <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#888]">Stripe Publishable Key (pk_...)</label>
          <Input type="text" value={publishable} onChange={(e) => setPublishable(e.target.value)}
            placeholder="pk_live_... oswa pk_test_..." data-testid="api-keys-publishable-input"
            className="mt-1.5 bg-[#0A0A0A] border-[#222] h-11 rounded-sm font-mono-term" />
        </div>
        <Button onClick={save} disabled={busy} data-testid="api-keys-save-button"
          className="h-11 rounded-sm bg-[#00F593] text-black hover:bg-[#00d47f] font-semibold px-6">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Anrejistre Kle yo</>}
        </Button>
        <p className="text-[11px] text-[#666] leading-relaxed">
          Kle sa yo ranplase sa ki nan .env la epi yo aplike touswit. Kite yon chan vid pou pa chanje l.
        </p>
      </div>
    </div>
  );
}
